import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { inr, inr2, num } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { Panel, Loading, Pill } from "@/components/kit";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
} from "recharts";
import {
  Sparkles, Target, TrendingUp, RotateCcw, Save, Trophy, ArrowUpRight, ArrowDownRight,
  Layers, Gauge, Flag, Trash2, Lightbulb, GitCompare, Info, SlidersHorizontal,
  ChevronDown, ChevronUp,
} from "lucide-react";

const sliderMax = (base) => Math.max(Math.ceil((base * 2.5) / 50) * 50, 500);
const CONFETTI_COLORS = ["#F59E0B", "#D97706", "#00A3E0", "#059669", "#00529B", "#FBBF24"];

function tierLabel(t, nt) {
  if (!nt) return "";
  if (nt.type === "slab") return t("nextSlab", { thr: num(nt.threshold_mt) });
  if (nt.type === "block") return t("nextBlock", { gap: num(nt.gap_mt) });
  if (nt.type === "breakeven") return t("crossBase", { base: num(nt.threshold_mt) });
  return nt.label || "";
}

function Confetti({ fireKey }) {
  if (!fireKey) return null;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" key={fireKey}>
      {Array.from({ length: 16 }).map((_, i) => (
        <span key={i} className="confetti-piece"
          style={{
            left: `${8 + (i * 5.5)}%`,
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animationDelay: `${(i % 5) * 0.06}s`,
          }} />
      ))}
    </div>
  );
}

export default function DealerPlayground() {
  const { t } = useLang();
  const { data: meta, isLoading } = useQuery({
    queryKey: ["dealer-simulator"],
    queryFn: async () => (await api.get("/dealer/simulator")).data,
    retry: false,
  });

  const [qty, setQty] = useState({});
  const [sim, setSim] = useState(null);
  const debounceRef = useRef();
  const [showHelp, setShowHelp] = useState(true);

  const [goalInput, setGoalInput] = useState({});
  const [goalResult, setGoalResult] = useState({});

  const [saveOpen, setSaveOpen] = useState(false);
  const [scenarioName, setScenarioName] = useState("");
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [scenarios, setScenarios] = useState([]);

  // celebratory confetti when upside grows
  const [confettiKey, setConfettiKey] = useState(0);
  const prevUpliftRef = useRef(0);

  const ENGINE_LABEL = { QD: t("engineQD"), GROWTH: t("engineGROWTH"), MILESTONE: t("engineMILESTONE") };
  const HOW_IT_WORKS = [
    { icon: SlidersHorizontal, title: t("s1t"), body: t("s1b") },
    { icon: Flag, title: t("s2t"), body: t("s2b") },
    { icon: Trophy, title: t("s3t"), body: t("s3b") },
    { icon: Target, title: t("s4t"), body: t("s4b") },
    { icon: Save, title: t("s5t"), body: t("s5b") },
  ];

  const loadScenarios = async () => {
    try { setScenarios((await api.get("/dealer/scenarios")).data.scenarios || []); } catch (e) {}
  };
  useEffect(() => { loadScenarios(); }, []);

  useEffect(() => {
    if (meta?.schemes) {
      const init = {};
      meta.schemes.forEach((s) => { init[s.scheme_id] = s.baseline_qty; });
      setQty(init);
    }
  }, [meta]);

  useEffect(() => {
    if (!meta?.schemes?.length || !Object.keys(qty).length) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const items = Object.entries(qty).map(([scheme_id, v]) => ({ scheme_id, projected_qty_mt: Number(v) || 0 }));
        const res = await api.post("/dealer/simulate", { items });
        setSim(res.data);
        const up = res.data?.totals?.uplift_total || 0;
        if (up > 0 && up > prevUpliftRef.current + 0.5) setConfettiKey((k) => k + 1);
        prevUpliftRef.current = up;
      } catch (e) {}
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [qty, meta]);

  const rowMap = useMemo(() => {
    const m = {};
    (sim?.rows || []).forEach((r) => { m[r.scheme_id] = r; });
    return m;
  }, [sim]);

  const setQtyFor = (id, v, max) => {
    let n = Number(v);
    if (isNaN(n)) n = 0;
    n = Math.max(0, Math.min(n, max));
    setQty((p) => ({ ...p, [id]: n }));
  };

  const resetBaseline = () => {
    const init = {};
    meta.schemes.forEach((s) => { init[s.scheme_id] = s.baseline_qty; });
    setQty(init);
    toast.success(t("resetToast"));
  };

  const runGoalSeek = async (schemeId) => {
    const target = Number(goalInput[schemeId]);
    if (!target || target <= 0) { toast.error(t("goalEnterFirst")); return; }
    try {
      const res = await api.post("/dealer/goal-seek", { scheme_id: schemeId, target_amount: target });
      setGoalResult((p) => ({ ...p, [schemeId]: res.data }));
      if (res.data.achievable && res.data.required_qty_mt != null) {
        setQtyFor(schemeId, res.data.required_qty_mt, sliderMax(rowMap[schemeId]?.baseline_qty_mt || 0));
      }
    } catch (e) { toast.error("—"); }
  };

  const saveScenario = async () => {
    const name = scenarioName.trim();
    if (!name) { toast.error(t("nameRequired")); return; }
    try {
      const items = Object.entries(qty).map(([scheme_id, v]) => ({ scheme_id, projected_qty_mt: Number(v) || 0 }));
      await api.post("/dealer/scenarios", { name, items });
      toast.success(t("savedToast", { name }));
      setScenarioName(""); setSaveOpen(false);
      loadScenarios();
    } catch (e) { toast.error("—"); }
  };

  const applyScenario = (sc) => {
    const next = { ...qty };
    (sc.items || []).forEach((it) => { next[it.scheme_id] = it.projected_qty_mt; });
    setQty(next);
    toast.success(t("loadedToast", { name: sc.name }));
  };

  const deleteScenario = async (id) => {
    try { await api.delete(`/dealer/scenarios/${id}`); loadScenarios(); toast.success(t("deletedToast")); } catch (e) {}
  };

  const openCompare = async () => {
    setCompareOpen(true); setCompareData(null);
    try {
      const baselineItems = meta.schemes.map((s) => ({ scheme_id: s.scheme_id, projected_qty_mt: s.baseline_qty }));
      const currentItems = Object.entries(qty).map(([scheme_id, v]) => ({ scheme_id, projected_qty_mt: Number(v) || 0 }));
      const jobs = [
        { name: t("baselineActual"), items: baselineItems, kind: "baseline" },
        { name: t("currentSandbox"), items: currentItems, kind: "current" },
        ...scenarios.map((sc) => ({ name: sc.name, items: sc.items || [], kind: "saved" })),
      ];
      const results = await Promise.all(
        jobs.map((j) => api.post("/dealer/simulate", { items: j.items }).then((r) => ({
          name: j.name, kind: j.kind, total: r.data.totals.projected_total,
        })))
      );
      const base = results.find((r) => r.kind === "baseline")?.total || 0;
      setCompareData(results.map((r) => ({ ...r, uplift: Math.round((r.total - base) * 100) / 100 })));
    } catch (e) { toast.error("—"); }
  };

  if (isLoading) return <Loading label={t("preparing")} />;
  if (!meta?.schemes?.length) {
    return (
      <Panel testid="playground-empty">
        <div className="py-16 text-center text-slate-400">
          <Sparkles className="mx-auto mb-3 text-slate-300" size={32} />
          {t("emptyTitle")}
        </div>
      </Panel>
    );
  }

  const totals = sim?.totals;
  const upliftPositive = (totals?.uplift_total || 0) >= 0;

  return (
    <div className="space-y-6" data-testid="dealer-playground">
      {/* Intro / disclaimer + how it works */}
      <div className="bg-[#00529B]/5 border border-[#00529B]/15 rounded-2xl overflow-hidden" data-testid="playground-disclaimer">
        <div className="flex items-start gap-3 p-4">
          <div className="w-9 h-9 rounded-lg bg-[#00529B]/10 flex items-center justify-center text-[#00529B] shrink-0">
            <Info size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-semibold text-slate-800 text-sm">{t("projectionTitle")}</div>
            <p className="text-xs text-slate-500 mt-0.5">{t("disclaimer")}</p>
          </div>
          <button onClick={() => setShowHelp((v) => !v)} data-testid="pg-help-toggle"
            className="shrink-0 flex items-center gap-1 text-xs font-semibold text-[#00529B] hover:text-[#0F2C59] px-2 py-1 rounded-md hover:bg-[#00529B]/10">
            {showHelp ? <>{t("hideGuide")} <ChevronUp size={14} /></> : <>{t("howItWorks")} <ChevronDown size={14} /></>}
          </button>
        </div>
        {showHelp && (
          <div className="border-t border-[#00529B]/15 bg-white/60 px-4 py-4" data-testid="pg-how-it-works">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {HOW_IT_WORKS.map((h, i) => (
                <div key={i} className="flex items-start gap-2.5 rounded-lg bg-white border border-slate-100 p-3 shadow-sm">
                  <div className="w-8 h-8 rounded-lg bg-[#00529B]/10 flex items-center justify-center text-[#00529B] shrink-0">
                    <h.icon size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-800">{h.title}</div>
                    <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{h.body}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-2.5 rounded-lg bg-amber-50 border border-amber-200 p-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                  <Lightbulb size={16} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-amber-900">{t("tip")}</div>
                  <p className="text-[11px] text-amber-800 leading-snug mt-0.5">{t("tipBody")}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Summary + actions */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl p-5 bg-white border border-slate-200 shadow-sm" data-testid="pg-baseline-total">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">{t("baseline")}</div>
            <div className="font-heading text-2xl font-black mt-1.5 text-slate-800">{inr(totals?.baseline_total)}</div>
            <div className="text-xs text-slate-400 mt-1">{t("baselineSub")}</div>
          </div>
          <div className="rounded-2xl p-5 text-white shadow-md" style={{ background: "linear-gradient(135deg, #0F2C59 0%, #003366 55%, #00529B 120%)" }} data-testid="pg-projected-total">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-white/70">{t("projected")}</div>
            <div key={totals?.projected_total} className="font-heading text-2xl font-black mt-1.5 count-anim">{inr(totals?.projected_total)}</div>
            <div className="text-xs text-white/70 mt-1">{t("projectedSub")}</div>
          </div>
          <div className={`relative overflow-hidden rounded-2xl p-5 shadow-md text-white ${upliftPositive && (totals?.uplift_total || 0) > 0 ? "gold-pulse" : ""}`}
            style={{ background: upliftPositive ? "linear-gradient(135deg, #047857 0%, #059669 100%)" : "linear-gradient(135deg, #B91C1C 0%, #DC2626 100%)" }}
            data-testid="pg-uplift-total">
            <Confetti fireKey={confettiKey} />
            <div className="relative z-10">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-white/70">{t("upside")}</div>
              <div key={totals?.uplift_total} className="font-heading text-2xl font-black mt-1.5 flex items-center gap-1.5 count-anim">
                {upliftPositive ? <ArrowUpRight size={22} /> : <ArrowDownRight size={22} />}
                {inr(Math.abs(totals?.uplift_total || 0))}
              </div>
              <div className="text-xs text-white/70 mt-1">{t("upsideSub")}</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-4 flex flex-wrap items-center gap-2 lg:justify-end">
          <Button variant="outline" onClick={resetBaseline} data-testid="pg-reset-btn" className="gap-1.5 active:scale-95">
            <RotateCcw size={15} /> {t("reset")}
          </Button>
          <Button variant="outline" onClick={openCompare} data-testid="pg-compare-btn" className="gap-1.5 active:scale-95">
            <GitCompare size={15} /> {t("compare")}
          </Button>
          <Button onClick={() => setSaveOpen(true)} data-testid="pg-save-btn" className="gap-1.5 bg-[#0F2C59] hover:bg-[#00529B] active:scale-95">
            <Save size={15} /> {t("save")}
          </Button>
        </div>
      </div>

      {/* Saved scenarios chips */}
      {scenarios.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="pg-scenario-chips">
          <span className="text-xs text-slate-400 font-medium">{t("savedScenarios")}</span>
          {scenarios.map((sc) => (
            <div key={sc.id} className="group flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-white border border-slate-200 shadow-sm text-xs font-medium text-slate-700">
              <button onClick={() => applyScenario(sc)} data-testid={`pg-apply-${sc.id}`} className="hover:text-[#00529B]">{sc.name}</button>
              <button onClick={() => deleteScenario(sc.id)} data-testid={`pg-delete-${sc.id}`} className="w-5 h-5 rounded-full flex items-center justify-center text-slate-300 hover:text-[#DC2626] hover:bg-red-50">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Optimizer */}
      {sim?.optimizer?.length > 0 && (
        <div className="rounded-2xl p-5 border border-amber-200/80 shadow-sm" style={{ background: "linear-gradient(135deg, rgba(254,243,199,0.5) 0%, #ffffff 45%, rgba(254,243,199,0.25) 100%)" }} data-testid="pg-optimizer">
          <div className="flex items-center gap-2 mb-1">
            <Trophy size={18} className="text-[#D97706]" />
            <h3 className="font-heading font-semibold text-slate-800">{t("optimizer")}</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">{t("optimizerSub")}</p>
          <div className="space-y-2.5">
            {sim.optimizer.map((o, i) => (
              <div key={o.scheme_id} data-testid={`pg-optimizer-${i}`}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-all ${i === 0 ? "text-white border-transparent shadow-sm" : "bg-white/70 border-slate-100"}`}
                style={i === 0 ? { background: "linear-gradient(135deg, #D97706 0%, #F59E0B 100%)" } : {}}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${i === 0 ? "bg-white/25 text-white" : "bg-white text-slate-400 border border-slate-200"}`}>
                  {i === 0 ? <Trophy size={16} /> : <span className="text-xs font-bold">{i + 1}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold truncate ${i === 0 ? "text-white" : "text-slate-800"}`}>{o.scheme_name} <span className={`font-mono text-[11px] ${i === 0 ? "text-white/70" : "text-slate-400"}`}>· {o.scheme_code}</span></div>
                  {o.next_tier && <div className={`text-[11px] truncate ${i === 0 ? "text-white/80" : "text-slate-500"}`}>{tierLabel(t, o.next_tier)}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-sm font-bold ${i === 0 ? "text-white" : "text-[#059669]"}`}>{inr2(o.marginal_per_mt)}</div>
                  <div className={`text-[10px] uppercase tracking-wide ${i === 0 ? "text-white/70" : "text-slate-400"}`}>{t("perExtraMT")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-scheme simulators */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {meta.schemes.map((s) => (
          <SchemeSimCard
            key={s.scheme_id}
            t={t}
            engineLabel={ENGINE_LABEL[s.engine] || s.engine}
            scheme={s}
            row={rowMap[s.scheme_id]}
            qty={qty[s.scheme_id] ?? s.baseline_qty}
            onQty={(v) => setQtyFor(s.scheme_id, v, sliderMax(s.baseline_qty))}
            goalInput={goalInput[s.scheme_id] || ""}
            setGoalInput={(v) => setGoalInput((p) => ({ ...p, [s.scheme_id]: v }))}
            goalResult={goalResult[s.scheme_id]}
            onGoalSeek={() => runGoalSeek(s.scheme_id)}
          />
        ))}
      </div>

      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="pg-save-dialog">
          <DialogHeader>
            <DialogTitle>{t("saveDialogTitle")}</DialogTitle>
            <DialogDescription>{t("saveDialogDesc")}</DialogDescription>
          </DialogHeader>
          <Input autoFocus placeholder={t("scenarioPlaceholder")} value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveScenario()}
            data-testid="pg-scenario-name-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>{t("cancel")}</Button>
            <Button onClick={saveScenario} data-testid="pg-scenario-save-confirm" className="bg-[#0F2C59] hover:bg-[#00529B]">{t("saveBtn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-2xl" data-testid="pg-compare-dialog">
          <DialogHeader>
            <DialogTitle>{t("compareTitle")}</DialogTitle>
            <DialogDescription>{t("compareDesc")}</DialogDescription>
          </DialogHeader>
          {!compareData ? <Loading label={t("loadingCompare")} /> : (
            <div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={compareData} margin={{ left: 10, right: 20, top: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>
                    <LabelList dataKey="total" position="top" formatter={(v) => inr(v)} style={{ fontSize: 10, fill: "#334155" }} />
                    {compareData.map((d, i) => (
                      <Cell key={i} fill={d.kind === "baseline" ? "#94a3b8" : d.kind === "current" ? "#00529B" : "#059669"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1.5">
                {compareData.filter((d) => d.kind !== "baseline").map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-slate-50">
                    <span className="font-medium text-slate-700">{d.name}</span>
                    <span className={`font-semibold ${d.uplift >= 0 ? "text-[#059669]" : "text-[#DC2626]"}`}>
                      {d.uplift >= 0 ? "+" : "−"}{inr(Math.abs(d.uplift))} {t("vsBaseline")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SchemeSimCard({ t, engineLabel, scheme, row, qty, onQty, goalInput, setGoalInput, goalResult, onGoalSeek }) {
  const max = sliderMax(scheme.baseline_qty);
  const incentive = row?.incentive ?? scheme.baseline_incentive;
  const uplift = row?.uplift ?? 0;
  const up = uplift >= 0;
  const nextTier = row?.next_tier;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden fade-up" data-testid={`pg-card-${scheme.scheme_code}`}>
      <div className="px-5 py-4 border-b border-slate-200/60 flex items-start justify-between gap-3" style={{ background: "linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%)" }}>
        <div className="min-w-0">
          <div className="font-heading font-semibold text-slate-800 truncate">{scheme.scheme_name}</div>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-mono text-[11px] text-slate-400">{scheme.scheme_code}</span>
            <Pill>{engineLabel}</Pill>
            {scheme.period && <span className="text-[11px] text-slate-400">{scheme.period}</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`font-heading text-xl font-black text-[#00529B] rounded-lg px-1 ${uplift > 0 ? "gold-pulse" : ""}`} data-testid={`pg-incentive-${scheme.scheme_code}`}>{inr(incentive)}</div>
          <div className={`text-xs font-semibold inline-flex items-center gap-0.5 ${up ? "text-[#059669]" : "text-[#DC2626]"}`}>
            {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{up ? "+" : "−"}{inr(Math.abs(uplift))}
          </div>
        </div>
      </div>

      <div className="p-5 space-y-5">
        <div className="bg-slate-50/70 rounded-xl p-4 border border-slate-200/60">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t("projectedVolume")}</label>
            <div className="flex items-center gap-2">
              <Input type="number" value={Number(qty).toFixed(0)} onChange={(e) => onQty(e.target.value)}
                className="w-24 h-8 text-right text-sm" data-testid={`pg-qty-input-${scheme.scheme_code}`} />
              <span className="text-sm text-slate-400 font-medium">MT</span>
            </div>
          </div>
          <Slider value={[Math.min(Number(qty), max)]} min={0} max={max} step={1}
            onValueChange={(v) => onQty(v[0])} data-testid={`pg-slider-${scheme.scheme_code}`} />
          <div className="flex justify-between text-[10px] text-slate-400 mt-1">
            <span>0</span>
            <span className="text-slate-500">{t("baselineWord")} {num(scheme.baseline_qty)} MT</span>
            <span>{num(max)}</span>
          </div>
        </div>

        {nextTier && nextTier.gap_mt > 0 && (
          <div className="flex items-center gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5" data-testid={`pg-nexttier-${scheme.scheme_code}`}>
            <Flag size={16} className="text-amber-600 shrink-0" />
            <div className="text-xs text-amber-900 flex-1">{tierLabel(t, nextTier)}</div>
            {nextTier.extra_incentive > 0 && (
              <div className="text-xs font-bold text-amber-700 shrink-0">+{inr(nextTier.extra_incentive)}</div>
            )}
          </div>
        )}

        <EngineDeepDive t={t} scheme={scheme} row={row} qty={Number(qty)} />

        <div className="rounded-xl bg-slate-100/60 border border-slate-200/70 p-3.5">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-2">
            <Target size={14} className="text-[#00529B]" /> {t("goalTitle")}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">₹</span>
            <Input type="number" placeholder={t("goalPlaceholder")} value={goalInput}
              onChange={(e) => setGoalInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onGoalSeek()}
              className="h-8 text-sm flex-1" data-testid={`pg-goal-input-${scheme.scheme_code}`} />
            <Button size="sm" onClick={onGoalSeek} data-testid={`pg-goal-btn-${scheme.scheme_code}`} className="h-8 bg-[#0F2C59] hover:bg-[#00529B]">{t("find")}</Button>
          </div>
          {goalResult && (
            <div className="mt-2 text-xs" data-testid={`pg-goal-result-${scheme.scheme_code}`}>
              {goalResult.achievable ? (
                <span className="text-slate-600">{t("goalNeed", { qty: num(goalResult.required_qty_mt), amt: inr(goalResult.incentive_at) })}</span>
              ) : (
                <span className="text-[#DC2626]">{t("goalUnreachable")}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EngineDeepDive({ t, scheme, row, qty }) {
  const res = row?.result;
  if (scheme.engine === "QD") {
    const slabs = scheme.params?.slabs || [];
    const bags = scheme.params?.bags_per_mt || 1;
    return (
      <div data-testid={`pg-qd-ladder-${scheme.scheme_code}`}>
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-2">
          <Layers size={14} className="text-[#00529B]" /> {t("slabLadder")} <span className="text-slate-400 font-normal">· {bags} bags/MT</span>
        </div>
        <div className="space-y-1.5">
          {slabs.map((sl, i) => {
            const active = qty >= (sl.from_mt || 0) && (sl.to_mt == null || qty < sl.to_mt);
            return (
              <div key={i} className={`flex items-center justify-between text-xs rounded-md px-2.5 py-1.5 border ${active ? "bg-[#00529B]/8 border-[#00529B]/30 text-[#00529B] font-semibold" : "bg-white border-slate-100 text-slate-500"}`}>
                <span>{num(sl.from_mt || 0)}{sl.to_mt == null ? "+" : ` – ${num(sl.to_mt)}`} MT</span>
                <span className="flex items-center gap-2">
                  ₹{sl.rate_per_mt}/bag
                  {active && <span className="text-[10px] uppercase bg-[#00529B] text-white px-1.5 py-0.5 rounded-full">{t("youreHere")}</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  if (scheme.engine === "MILESTONE") {
    const block = scheme.params?.block_size_mt || 25;
    const completed = res?.completed_blocks ?? Math.floor(qty / block);
    const within = block > 0 ? ((qty % block) / block) * 100 : 0;
    const isFallback = res?.mode === "fallback";
    return (
      <div data-testid={`pg-milestone-${scheme.scheme_code}`}>
        <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-2">
          <span className="flex items-center gap-1.5"><TrendingUp size={14} className="text-[#00529B]" /> {t("blockProgress")}</span>
          <span className="text-slate-400 font-normal">{t("blocksDone", { n: completed })} · ₹{scheme.params?.bonus_per_block}/block</span>
        </div>
        <Progress value={isFallback ? Math.min(100, (qty / block) * 100) : within} className="h-2" />
        <div className="text-[11px] text-slate-400 mt-1">
          {t("milestoneUnlock", { block: num(block) })} ₹{scheme.params?.bonus_per_block}
        </div>
      </div>
    );
  }
  const base = scheme.params?.base_target_mt || 0;
  const growthPct = res?.growth_pct ?? 0;
  return (
    <div data-testid={`pg-growth-${scheme.scheme_code}`}>
      <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-2">
        <span className="flex items-center gap-1.5"><Gauge size={14} className="text-[#00529B]" /> {t("growthOverBase")}</span>
        <span className="text-slate-400 font-normal">{t("baselineWord")} {num(base)} MT · ₹{scheme.params?.rate_per_mt}/MT</span>
      </div>
      <Progress value={Math.max(0, Math.min(100, growthPct))} className="h-2" />
      <div className="flex justify-between text-[11px] text-slate-400 mt-1">
        <span className={growthPct >= 0 ? "text-[#059669] font-semibold" : "text-[#DC2626] font-semibold"}>{growthPct >= 0 ? "+" : ""}{growthPct}% {t("vsBase")}</span>
      </div>
    </div>
  );
}
